/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useTranslation } from "react-i18next";
import { ReactComponent as Info } from "../../../assets/svg/info.svg";
import { FormValidatorKeysEnum } from "../../../enums/form";
import { TooltipWrapper } from "../../shared/Header/AccountModal/style";
import { Input } from "../../shared/Input";
import TooltipExplanation from "../../shared/Tooltip";
import { InputWrapper, Label, WarningLabel } from "../style";
import { sanitizedRegister } from "./sanitizedRegister";
import { LabelFlexWrapper } from "./style";
import {
  IPriceInputProps,
  ITriggerPriceInputProps,
  PerpFormFieldKeyEnum,
} from "./form.types";

export function PriceInput({
  register,
  errors,
  warning,
  minPrice,
  placeholder,
  isStopOrder,
  rightAccessory,
}: IPriceInputProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.Form",
  });
  const { t: tooltips } = useTranslation("tooltips");
  const { t: formError } = useTranslation("formErrors", { keyPrefix: "price" });
  const error = errors?.[PerpFormFieldKeyEnum.PRICE];
  const hasError = Boolean(error?.type);
  return (
    <InputWrapper>
      <LabelFlexWrapper>
        <Label>{t("limit_price")}</Label>
        {isStopOrder && (
          <TooltipExplanation
            title={t("limit_price")}
            explanation={tooltips("stopLimitPrice")}
            renderContent={({ ref, ...triggerHandler }) => (
              <TooltipWrapper ref={ref} {...triggerHandler}>
                <Info />
              </TooltipWrapper>
            )}
          />
        )}
      </LabelFlexWrapper>
      <Input
        error={hasError}
        placeholder={placeholder ?? "0"}
        // NOT type="number": Chrome parses it against the browser locale and
        // silently drops a comma, so "70000,55" arrived as 7000055 -- a 100x
        // limit price, with the CTA enabled. The shared Input defaults to text
        // and says so; this field used to opt back in.
        inputMode="decimal"
        // The visual <Label> is a styled SPAN, so htmlFor cannot associate it.
        // form.TpslControls.tsx already settled this question for this exact
        // Label component: keep the span, name the input. Hardcoded English, as
        // there, so the jest i18n stub and Playwright agree.
        aria-label="Limit price"
        rightAccessory={rightAccessory}
        {...sanitizedRegister(register)}
      />
      {error?.type === FormValidatorKeysEnum.required && (
        <WarningLabel>{formError("required")}</WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.moreThanZero && (
        <WarningLabel>{formError("moreThanZero")}</WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.decimalsTooSmall && (
        <WarningLabel>
          {formError("cannotBeLessThanSize", { size: minPrice })}
        </WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.limitPriceAboveTriggerPrice && (
        <WarningLabel>{formError("priceAboveTriggerPrice")}</WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.limitPriceBelowTriggerPrice && (
        <WarningLabel>{formError("priceBelowTriggerPrice")}</WarningLabel>
      )}
      {Boolean(!Object.keys(errors).length && warning) && (
        <WarningLabel $type="warning">{warning}</WarningLabel>
      )}
    </InputWrapper>
  );
}

export function TriggerPriceInput({
  register,
  errors,
  warning,
  minPrice,
  placeholder,
  markPrice,
}: ITriggerPriceInputProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.Form",
  });
  const { t: tooltips } = useTranslation("tooltips");
  const { t: formError } = useTranslation("formErrors", {
    keyPrefix: "triggerPrice",
  });
  const error = errors?.[PerpFormFieldKeyEnum.TRIGGER_PRICE];
  const hasError = Boolean(error?.type);
  return (
    <InputWrapper>
      <LabelFlexWrapper>
        <Label>{t("trigger_price")}</Label>
        <TooltipExplanation
          title={t("trigger_price")}
          explanation={tooltips("stopTriggerPrice")}
          renderContent={({ ref, ...triggerHandler }) => (
            <TooltipWrapper ref={ref} {...triggerHandler}>
              <Info />
            </TooltipWrapper>
          )}
        />
      </LabelFlexWrapper>
      <Input
        error={hasError}
        placeholder={placeholder ?? "0"}
        // Same reason as the limit price above: a trigger price is money.
        inputMode="decimal"
        aria-label="Trigger price"
        {...sanitizedRegister(register)}
      />
      {error?.type === FormValidatorKeysEnum.required && (
        <WarningLabel>{formError("required")}</WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.moreThanZero && (
        <WarningLabel>{formError("moreThanZero")}</WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.decimalsTooSmall && (
        <WarningLabel>
          {formError("cannotBeLessThanSize", { size: minPrice })}
        </WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.triggerPriceAboveMark && (
        <WarningLabel>
          {formError("priceAboveMarkPrice", {
            markPrice: markPrice || t("mark_price"),
          })}
        </WarningLabel>
      )}
      {error?.type === FormValidatorKeysEnum.triggerPriceBelowMark && (
        <WarningLabel>
          {formError("priceBelowMarkPrice", {
            markPrice: markPrice || t("mark_price"),
          })}
        </WarningLabel>
      )}
      {Boolean(!Object.keys(errors).length && warning) && (
        <WarningLabel $type="warning">{warning}</WarningLabel>
      )}
    </InputWrapper>
  );
}
